/**
 * useFileDrop — lets a box take a dropped file as well as a picked one.
 *
 * Why a hook beside UploadFileButton: the button stays the one way to open the
 * file picker; this adds drag and drop to the box around it (the empty
 * instance card, a dialog's file slot), through the SAME checks and the SAME
 * `onFile` / `onReject` hand-offs, so a dropped file is judged exactly like a
 * picked one (.xlsx only, first file only).
 *
 * `max` > 1 (EIA: the instances file and the Self Perf Assessment file may be
 * dropped together, user order 2026-10-26): up to `max` files are taken, in the
 * order the browser lists them, and handed to `onFiles` as an array — the first
 * is the instances file, the second the self-perf one. The drop is refused as a
 * whole when it has more than `max` files or any of them is not .xlsx, so the
 * order can never end up pairing the wrong file with a slot.
 *
 * Spread `dropProps` on the box and add a class while `dragging` is true.
 * Nothing happens for a drag that carries no files (selected text, a link),
 * and a disabled box ignores drops and lets the browser do its default.
 */
import { useRef, useState } from 'react';

import { isAllowedUpload } from './portfolioFormRules';
import { UPLOAD_REJECTED_MESSAGE } from './UploadFileButton';

const hasFiles = (e) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

export function useFileDrop({ onFile, onFiles, onReject, disabled = false, max = 1 }) {
  const [dragging, setDragging] = useState(false);
  // dragenter / dragleave also fire for every child the pointer crosses; a
  // counter keeps the highlight steady instead of flickering.
  const depth = useRef(0);

  const reset = () => {
    depth.current = 0;
    setDragging(false);
  };

  const dropProps = {
    onDragEnter: (e) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setDragging(true);
    },
    onDragOver: (e) => {
      if (disabled || !hasFiles(e)) return;
      // Without this the browser refuses the drop.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e) => {
      if (disabled || !hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e) => {
      if (disabled || !hasFiles(e)) return;
      // Stops the browser opening the file in the tab.
      e.preventDefault();
      reset();
      const dropped = Array.from(e.dataTransfer.files ?? []);
      if (dropped.length === 0) return;
      if (max > 1) {
        if (dropped.length > max) {
          onReject?.(`You can drop up to ${max} files at once.`);
          return;
        }
        if (!dropped.every(isAllowedUpload)) {
          onReject?.(UPLOAD_REJECTED_MESSAGE);
          return;
        }
        if (onFiles) onFiles(dropped);
        else onFile?.(dropped[0]);
        return;
      }
      const file = dropped[0];
      if (!isAllowedUpload(file)) {
        onReject?.(UPLOAD_REJECTED_MESSAGE);
        return;
      }
      onFile?.(file);
    },
  };

  return { dropProps, dragging: dragging && !disabled };
}
